import { eventsService } from '@chilipiper/service/src/events'
import React, { ComponentType, useCallback, useEffect, useMemo, useState } from 'react'
import { Workspace } from '@chilipiper/api-type-def'
import { Workspaces } from '@chilipiper/models'
import { defaultErrorCallback } from '@chilipiper/service/src/api'
import { ApiError } from '@chilipiper/service/src/types'
import { createContextWithHook } from '@chilipiper/utils'

type WorkspaceWithV2Assets = Workspace & {
  isV2Assets?: boolean // TODO: https://floatingapps.atlassian.net/browse/CPP-9298
  // Remove this. Temporary flag while both versions of rules, teams and assignment rulesets are being implemented.
}

const initialState: WorkspaceWithV2Assets = {
  active: false,
  color: '',
  default: false,
  deleted: false,
  integrations: ['Salesforce', 'Convey', 'Hubspot'],
  order: 0,
  productTypes: { meetingsType: 'Hot' },
  selected: false,
  usersCount: 0,
  workspaceType: 'CRM',
  link: '',
  id: '',
  name: '',
}

const { useSetWorkspaceContext, SetWorkspaceBaseProvider } =
  createContextWithHook<(workspace: WorkspaceWithV2Assets) => void>()('SetWorkspace')
const { useWorkspaceContext, WorkspaceBaseProvider } =
  createContextWithHook<WorkspaceWithV2Assets>()('Workspace')
export {
  useSetWorkspaceContext as useSetWorkspace,
  WorkspaceBaseProvider,
  SetWorkspaceBaseProvider,
}

interface Props {
  cfa?: boolean
  children: React.ReactNode
  id?: string
}

export const WorkspaceProvider = ({ children, id, cfa }: Props) => {
  const [workspace, setWorkspace] = useState<WorkspaceWithV2Assets>(initialState)

  const loadWorkspace = useCallback(async () => {
    if (!id) {
      return
    }
    const workspace = await Workspaces.getById(id)
    const isEventsWorkspace = Workspaces.isEventsType(workspace)
    const events = isEventsWorkspace ? await eventsService.getEventsByWorkspaceId(id) : []
    const event = events[0]
    setWorkspace({
      ...workspace,
      eventId: event?.id,
      event,
    })
  }, [id])

  const loadInfo = useCallback(async () => {
    if (!id) {
      return
    }

    try {
      const workspace = await Workspaces.getInfo(id)
      setWorkspace(workspace)
    } catch (error) {
      defaultErrorCallback(error as ApiError)
    }
  }, [id])

  useEffect(() => {
    if (cfa) {
      loadInfo()
    } else {
      loadWorkspace()
    }
  }, [loadInfo, loadWorkspace, cfa])

  return (
    <WorkspaceBaseProvider value={workspace}>
      <SetWorkspaceBaseProvider value={setWorkspace}>{children}</SetWorkspaceBaseProvider>
    </WorkspaceBaseProvider>
  )
}

export const useWorkspace = () => {
  const state = useWorkspaceContext()
  // TODO: Remove this when BE for this separation is implemented https://floatingapps.atlassian.net/browse/CPP-9298
  const stateWithIsV2 = useMemo(
    () => ({
      ...state,
      isV2Assets: state?.name?.includes('V2_ASSETS'),
    }),
    [state]
  )
  return stateWithIsV2
}

// TODO after https://floatingapps.atlassian.net/browse/CPP-9298 this can be replaced with the HOC exported by createContextWithHook
export const withWorkspace = (Component: ComponentType<{ workspace: WorkspaceWithV2Assets }>) => {
  const WrapperComponent = (props: object) => {
    const workspace = useWorkspace()
    return <Component {...props} workspace={workspace} />
  }
  return WrapperComponent
}
